import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  Card,
  CardBody,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Center,
  IconButton,
  Grid,
  Skeleton,
} from '@chakra-ui/react';
import {
  AddIcon,
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
} from '@chakra-ui/icons';
import {
  FaUserShield,
  FaUserTie,
  FaUserCog,
  FaUserCheck,
  FaUsers,
  FaThLarge,
  FaTable,
  FaList,
  FaCrown,
  FaUserSecret,
  FaUserTimes,
  FaUserTag,
} from 'react-icons/fa';
import { HiMiniUsers } from 'react-icons/hi2';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import AddUserModal from './AddUserModal';
import EditUserModal from './UpdateUserModal';
import ViewUserModal from './ViewUserModal';
import baseUrl from 'Base_Url/baseUrl';
import { ForbiddenPage } from '../Forbidden/Forbidden';
import axiosInstance from 'axiosInstance';
import { motion } from 'framer-motion';
import EnhancedUsersTable from './tables/EnhancedUsersTable';
import UserCard from './tables/UserCard';
import UserListItem from './tables/UserListItem';
const MotionBox = motion(Box);

const User = () => {
  const [users, setUsers] = useState([]);
  const [addUser, setAddUser] = useState({
    name: '',
    phoneNo: '',
    role: '',
    department: [],
    userId: '',
    schemes: [],
    tahsils: [],
    fileTypes: [],
    subDivision: '',
    nameOfOffice: '',
    designation: '',
  });
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [viewMode, setViewMode] = useState('table');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    admin: 0,
    department: 0,
    schemeAdmin: 0,
    talukaUser: 0,
    departmentOfficer: 0,
    projectOfficer: 0,
    subDivisionalOfficer: 0,
  });

  const token = localStorage.getItem('token');
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();

  const inputTextColor = useColorModeValue('gray.800', 'white');
  const bgColor = useColorModeValue(
    'linear(135deg, gray.50, blue.100, blue.100)',
    'linear(135deg, gray.800,gray.400, gray.500)',
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue('purple.500', 'purple.300');
  const loaderBg = useColorModeValue('gray.50', 'gray.900');
  const loaderPattern = useColorModeValue(
    'radial-gradient(circle at 25px 25px, rgba(120, 119, 198, 0.3) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(120, 119, 198, 0.3) 2%, transparent 0%)',
    'radial-gradient(circle at 25px 25px, rgba(168, 85, 247, 0.15) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(168, 85, 247, 0.15) 2%, transparent 0%)',
  );

  const loaderGradient = useColorModeValue(
    'linear(to-r, purple.400, pink.500)',
    'linear(to-r, purple.500, pink.600)',
  );
  
  const bgGradient = useColorModeValue(
    'linear(to-br, white, purple.50)',
    'linear(to-br, gray.900, purple.900)',
  );

  const roleOptions = [
    { value: 'admin', label: 'Admin', icon: FaUserShield },
    { value: 'departmentAdmin', label: 'Departmental Admin', icon: FaUserTie },
    { value: 'departmentUser', label: 'Departmental User', icon: FaUserCog },
    {
      value: 'departmentOfficer',
      label: 'Department Officer',
      icon: FaUserCheck,
    },
  ];

  const getUsers = async (searchString = search, pageNumber = page) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `${baseUrl}/auth/users?page=${pageNumber}&searchString=${encodeURIComponent(
          searchString,
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const responseData = res.data;

      let usersData = [];
      let statsData = {};

      if (responseData && responseData.data) {
        if (Array.isArray(responseData.data)) {
          usersData = responseData.data;
        }
        else if (
          responseData.data.data &&
          Array.isArray(responseData.data.data)
        ) {
          usersData = responseData.data.data;
        }

        if (responseData.data.stats) {
          statsData = responseData.data.stats;
        } else if (responseData.stats) {
          statsData = responseData.stats;
        }

        const totalPages =
          responseData.data.totalPages || responseData.totalPages || 1;
        setTotalPages(totalPages);
      }

      const processedUsers = usersData.map((user) => {
        let department = [];
        if (user.department) {
          if (Array.isArray(user.department)) {
            department = user.department;
          } else if (typeof user.department === 'object') {
            department = [user.department];
          }
        }

        let departmentNames = '';
        if (department.length > 0) {
          departmentNames = department
            .map((dept) => {
              if (typeof dept === 'object' && dept.name) {
                return dept.name;
              }
              return 'Department';
            })
            .join(', ');
        }

        return {
          ...user,
          department: department,
          departmentNames: departmentNames,
          getDepartmentName: () => departmentNames,
          getFirstDepartment: () =>
            department.length > 0 ? department[0] : null,
        };
      });

      setUsers(processedUsers || []);

      setStats({
        total: statsData.totalUsers || 0,
        active: statsData.activeUsers || 0,
        inactive: statsData.inactiveUsers || 0,
        admin: statsData.admin || 0,
        department: statsData.departmentUser || 0,
        schemeAdmin: statsData.schemeAdmin || 0,
        talukaUser: statsData.talukaUser || 0,
        departmentOfficer: statsData.departmentOfficer || 0,
        projectOfficer: statsData.projectOfficer || 0,
        subDivisionalOfficer: statsData.subDivisionalOfficer || 0,
      });
    } catch (err) {
      if (err.response?.status !== 401) {
        toast({
          title: 'Failed to load users',
          description:
            err.response?.data?.message || err.message || 'Unknown error',
          status: 'error',
          duration: 3000,
        });
      }

      setUsers([]);
      setStats({
        total: 0,
        active: 0,
        inactive: 0,
        admin: 0,
        department: 0,
        schemeAdmin: 0,
        talukaUser: 0,
        departmentOfficer: 0,
        projectOfficer: 0,
        subDivisionalOfficer: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setPage(1);
        const newParams = new URLSearchParams(searchParams);
        if (val) newParams.set('search', val);
        else newParams.delete('search');
        newParams.set('page', 1);
        setSearchParams(newParams);
      }, 500),
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    getUsers(searchParams.get('search') || '');
  }, [page, searchParams]);

  const handleAddUser = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        name: addUser.name,
        phoneNo: addUser.phoneNo,
        role: addUser.role,
        department: Array.isArray(addUser.department)
          ? addUser.department
          : addUser.department
          ? [addUser.department]
          : [],
        userId: addUser.userId,
      };

      if (addUser.role && addUser.role !== 'admin') {
        payload.nameOfOffice = addUser.nameOfOffice || '';
        payload.designation = addUser.designation || '';
      }

      if (addUser.role && addUser.role !== 'admin') {
        payload.schemes = Array.isArray(addUser.schemes)
          ? addUser.schemes
          : addUser.schemes
          ? [addUser.schemes]
          : [];
      }

      if (addUser.role === 'subDivisionalOfficer') {
        payload.subDivision = addUser.subDivision || '';
      }

      if (addUser.role === 'talukaUser') {
        payload.tahsils = Array.isArray(addUser.tahsils)
          ? addUser.tahsils
          : addUser.tahsils
          ? [addUser.tahsils]
          : [];
      }

      if (['projectOfficer', 'subDivisionalOfficer'].includes(addUser.role)) {
        payload.fileTypes = Array.isArray(addUser.fileTypes)
          ? addUser.fileTypes
          : addUser.fileTypes
          ? [addUser.fileTypes]
          : [];
      }

      const response = await axiosInstance.post(
        `${baseUrl}/auth/register`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast({
        title: 'User added successfully',
        status: 'success',
        position: 'top-right',
      });
      onClose();

      // Reset state
      setAddUser({
        name: '',
        phoneNo: '',
        role: '',
        department: [],
        userId: '',
        schemes: [],
        tahsils: [],
        fileTypes: [],
        subDivision: '',
        nameOfOffice: '',
        designation: '',
      });

      getUsers(search, 1);
    } catch (e) {
      console.error('Create User Error Details:', {
        error: e,
        response: e.response?.data,
        status: e.response?.status,
        headers: e.response?.headers,
      });

      if (e.response?.status !== 401) {
        toast({
          title:
            e.response?.data?.message ||
            e.response?.data?.data ||
            'Failed to add user',
          status: 'error',
          position: 'top-right',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [addUser, token, toast, onClose, getUsers, search]);

  const handleEditUser = (user) => {
    setEditUser(user);
    onEditOpen();
  };

  const closeEditModal = () => {
    setEditUser(null);
    onEditClose();
  };

  const handleRowClick = (user) => {
    setViewUser(user);
    onViewOpen();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage);
    setSearchParams(newParams);
  };

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUser = () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        setUser(userData);
      } catch (error) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    setTimeout(loadUser, 100);
  }, []);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  if (isLoading) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={loaderBg}
        position="relative"
        overflow="hidden"
        borderRadius="20px"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundImage={loaderPattern}
          backgroundSize="100px 100px"
        />

        <Flex direction="column" align="center" gap={6}>
          <Box
            width="100px"
            height="100px"
            borderRadius="full"
            bgGradient={loaderGradient}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaUsers} boxSize={12} color="white" />
          </Box>
        </Flex>
      </Flex>
    );
  }

  if (!user || user.role === 'talukaUser') {
    return <ForbiddenPage />;
  }

  return (
    <Box
      bgGradient={bgColor}
      minH="100vh"
      pt={{ base: '140px', md: '90px', xl: '90px', sm: '100px' }}
      px={{ base: '4', md: '4' }}
      borderRadius="2xl"
      pb="10px"
    >
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Flex
          direction="column"
          mb={3}
          p={6}
          borderRadius="2xl"
          bgGradient={bgGradient}
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.05)"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            w="200px"
            h="200px"
            borderRadius="full"
            bgGradient="linear(to-br, purple.200, pink.200)"
            opacity={0.1}
          /> 

          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            w="150px"
            h="150px"
            borderRadius="full"
            bgGradient="linear(to-br, teal.200, cyan.200)"
            opacity={0.1}
          />

          <Flex
            justify="space-between"
            align="center"
            mb={4}
            position="relative"
            zIndex={1}
          >
            <Flex align="center">
              <Flex
                align="center"
                justify="center"
                w="60px"
                h="60px"
                borderRadius="xl"
                bgGradient="linear(to-br, purple.500, pink.600)"
                mr={4}
                boxShadow="0 10px 30px rgba(168, 85, 247, 0.4)"
              >
                <Icon as={FaUsers} boxSize={7} color="white" />
              </Flex>
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  User Management
                </Text>
                <Text color={subtitleColor} fontSize="sm">
                  Manage user accounts, roles, and permissions
                </Text>
              </VStack>
            </Flex>

            <Button
              leftIcon={<AddIcon />}
              colorScheme="purple"
              onClick={onOpen}
              size="md"
              borderRadius="lg"
              bgGradient="linear(to-r, purple.500, pink.600)"
              _hover={{
                bgGradient: 'linear(to-r, purple.600, pink.700)',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 25px rgba(168, 85, 247, 0.3)',
              }}
              transition="all 0.2s"
              boxShadow="0 4px 15px rgba(168, 85, 247, 0.2)"
            >
              Add User
            </Button>
          </Flex>

          <SimpleGrid
            columns={{ base: 2, md: 4 }}
            spacing={4}
            mt={4}
            position="relative"
            zIndex={1}
          >
            <MotionBox
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Flex
                p={4}
                borderRadius="xl"
                bg={cardBg}
                align="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                borderColor="gray.100"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="45px"
                  h="45px"
                  borderRadius="lg"
                  bg="purple.100"
                  mr={3}
                >
                  <Icon as={FaUsers} color="purple.600" boxSize={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Total Users
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {stats.total}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>

            <MotionBox
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Flex
                p={4}
                borderRadius="xl"
                bg={cardBg}
                align="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                borderColor="gray.100"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="45px"
                  h="45px"
                  borderRadius="lg"
                  bg="green.100"
                  mr={3}
                >
                  <Icon as={CheckCircleIcon} color="green.600" boxSize={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Active Users
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {stats.active}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>

            <MotionBox
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Flex
                p={4}
                borderRadius="xl"
                bg={cardBg}
                align="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                borderColor="gray.100"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="45px"
                  h="45px"
                  borderRadius="lg"
                  bg="green.100"
                  mr={3}
                >
                  <Icon as={FaUserTimes} color="green.600" boxSize={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    InActive Users
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {stats.inactive}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>

            <MotionBox
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Flex
                p={4}
                borderRadius="xl"
                bg={cardBg}
                align="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                borderColor="gray.100"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="45px"
                  h="45px"
                  borderRadius="lg"
                  bg="blue.100"
                  mr={3}
                >
                  <Icon as={FaUserShield} color="blue.600" boxSize={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Office In Charge (OIC)
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {stats.schemeAdmin}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>

            <MotionBox
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Flex
                p={4}
                borderRadius="xl"
                bg={cardBg}
                align="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                borderColor="gray.100"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="45px"
                  h="45px"
                  borderRadius="lg"
                  bg="orange.100"
                  mr={3}
                >
                  <Icon as={FaUserSecret} color="orange.600" boxSize={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Sub Divisional Officer (SDO)
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {stats?.subDivisionalOfficer}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>

            <MotionBox
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Flex
                p={4}
                borderRadius="xl"
                bg={cardBg}
                align="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                borderColor="gray.100"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="45px"
                  h="45px"
                  borderRadius="lg"
                  bg="blue.100"
                  mr={3}
                >
                  <Icon as={FaUserTag} color="blue.600" boxSize={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Tehsildar
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {stats.talukaUser}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>
            <MotionBox
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Flex
                p={4}
                borderRadius="xl"
                bg={cardBg}
                align="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                borderColor="gray.100"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="45px"
                  h="45px"
                  borderRadius="lg"
                  bg="blue.100"
                  mr={3}
                >
                  <Icon as={FaCrown} color="blue.600" boxSize={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Project Officer (PO)
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {stats.projectOfficer}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>

            {user.role === 'admin' && (
              <>
                <MotionBox
                  whileHover={{ y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <Flex
                    p={4}
                    borderRadius="xl"
                    bg={cardBg}
                    align="center"
                    boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
                    borderColor="gray.100"
                  >
                    <Flex
                      align="center"
                      justify="center"
                      w="45px"
                      h="45px"
                      borderRadius="lg"
                      bg="blue.100"
                      mr={3}
                    >
                      <Icon as={FaUserTie} color="blue.600" boxSize={5} />
                    </Flex>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Dept. Officer
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        {stats.departmentOfficer}
                      </Text>
                    </Box>
                  </Flex>
                </MotionBox>
              </>
            )}
          </SimpleGrid>
        </Flex>
      </MotionBox>

      <Card mb={3} boxShadow="md" borderRadius="xl" bg={cardBg}>
        <CardBody>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            align={{ md: 'center' }}
          >
            <InputGroup
              size="sm"
              flex="1"
              width={{ base: '100%', sm: '200px' }}
            >
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={accentColor} />
              </InputLeftElement>
              <Input
                placeholder="Search users by name, role, or phone..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                color={inputTextColor}
                borderRadius="lg"
                border="2px solid"
                borderColor={borderColor}
                _focus={{
                  borderColor: accentColor,
                  boxShadow: `0 0 0 3px ${accentColor}33`,
                }}
                _hover={{
                  borderColor: accentColor,
                }}
                fontSize="sm"
              />
            </InputGroup>
            <HStack spacing={3}>
              <HStack spacing={1}>
                <IconButton
                  aria-label="Table View"
                  icon={<Icon as={FaTable} />}
                  size="sm"
                  colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
                  variant={viewMode === 'table' ? 'solid' : 'outline'}
                  onClick={() => handleViewModeChange('table')}
                  borderRadius="md"
                />
                <IconButton
                  aria-label="Card View"
                  icon={<Icon as={FaThLarge} />}
                  size="sm"
                  colorScheme={viewMode === 'card' ? 'blue' : 'gray'}
                  variant={viewMode === 'card' ? 'solid' : 'outline'}
                  onClick={() => handleViewModeChange('card')}
                  borderRadius="md"
                />
                <IconButton
                  aria-label="List View"
                  icon={<Icon as={FaList} />}
                  size="sm"
                  colorScheme={viewMode === 'list' ? 'blue' : 'gray'}
                  variant={viewMode === 'list' ? 'solid' : 'outline'}
                  onClick={() => handleViewModeChange('list')}
                  borderRadius="md"
                />
              </HStack>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      <Card boxShadow="lg" borderRadius="xl" overflow="hidden" bg={cardBg}>
        <CardBody p={0}>
          {loading ? (
            <Box p={8}>
              <Flex justify="center" align="center">
                <Spinner size="lg" color="blue.500" />
                <Text ml={4} fontSize="md" color={subtitleColor}>
                  Loading users...
                </Text>
              </Flex>
            </Box>
          ) : users.length === 0 ? (
            <Box p={12}>
              <Center>
                <VStack spacing={4}>
                  <Box
                    w="100px"
                    h="100px"
                    borderRadius="full"
                    bg="blue.50"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={HiMiniUsers} w={12} h={12} color="blue.300" />
                  </Box>
                  <VStack spacing={2}>
                    <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                      No users found
                    </Text>
                    <Text color={subtitleColor} textAlign="center" maxW="md">
                      Try adjusting your search or add a new user
                    </Text>
                  </VStack>
                </VStack>
              </Center>
            </Box>
          ) : viewMode === 'table' ? (
            <Box overflowX="auto">
              <EnhancedUsersTable
                tableData={users}
                handleEditUser={handleEditUser}
                handleRowClick={handleRowClick}
                handlePageChange={handlePageChange}
                page={page}
                totalPages={totalPages}
                loading={loading}
              />
            </Box>
          ) : viewMode === 'card' ? (
            <Box p={6}>
              <Grid
                templateColumns={{
                  base: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                }}
                gap={6}
              >
                {users.map((user) => (
                  <UserCard
                    key={user._id}
                    user={user}
                    handleEditUser={handleEditUser}
                    handleRowClick={handleRowClick}
                  />
                ))}
              </Grid>
            </Box>
          ) : viewMode === 'list' ? (
            <Box p={6}>
              <VStack spacing={4} align="stretch">
                {users.map((user) => (
                  <UserListItem
                    key={user._id}
                    user={user}
                    handleEditUser={handleEditUser}
                    handleRowClick={handleRowClick}
                  />
                ))}
              </VStack>
            </Box>
          ) : null}

          {totalPages > 1 && (
            <Flex
              mt={4}
              justify="space-between"
              align="center"
              p={4}
              borderTop="1px solid"
              borderColor={borderColor}
            >
              <Text fontSize="sm" color={subtitleColor}>
                Page {page} of {totalPages} • Showing {users.length} users
              </Text>
              <HStack spacing={2}>
                <IconButton
                  aria-label="Previous Page"
                  icon={<ChevronLeftIcon />}
                  isDisabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                  colorScheme="gray"
                  variant="outline"
                  size="sm"
                  borderRadius="md"
                />
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (page === 1) {
                    pageNum = i + 1;
                  } else if (page === totalPages) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = page - 1 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      colorScheme={page === pageNum ? 'blue' : 'gray'}
                      variant={page === pageNum ? 'solid' : 'outline'}
                      size="sm"
                      minW="8"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <IconButton
                  aria-label="Next Page"
                  icon={<ChevronRightIcon />}
                  isDisabled={page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  colorScheme="gray"
                  variant="outline"
                  size="sm"
                  borderRadius="md"
                />
              </HStack>
            </Flex>
          )}
        </CardBody>
      </Card>

      <AddUserModal
        isOpen={isOpen}
        onClose={onClose}
        addUser={addUser}
        setAddUser={setAddUser}
        handleAddUser={handleAddUser}
        loading={loading}
        roleOptions={roleOptions}
        renderRegionSelectors={() => null}
      />

      <EditUserModal
        isOpen={isEditOpen}
        onClose={closeEditModal}
        editUser={editUser}
        setEditUser={setEditUser}
        roleOptions={roleOptions}
        renderRegionSelectors={() => null}
        token={token}
        getUsers={getUsers}
      />

      <ViewUserModal
        isOpen={isViewOpen}
        onClose={onViewClose}
        viewUser={viewUser}
      />
    </Box>
  );
};

export default User;
